import React, {useEffect, useState} from 'react';
import {
    Alert,
    App,
    Button,
    DatePicker,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Steps,
    Table,
    Tag,
    Tooltip
} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {announcementService} from '../../services/announcementService';
import {Announcement, PageResponse} from '../../types/announcement';
import dayjs from 'dayjs';

const {Option} = Select;
const {TextArea} = Input;

const AnnouncementManagement: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
    const [form] = Form.useForm();
    const [formStep2] = Form.useForm();
    const [announcementType, setAnnouncementType] = useState<string>('platform');
    const [currentStep, setCurrentStep] = useState(0);
    const [overlappingWarnings, setOverlappingWarnings] = useState<string[]>([]);
    const [step1FormData, setStep1FormData] = useState<any>(null);

    // 分页状态
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = async (page: number = 0, size: number = 20) => {
        setLoading(true);
        try {
            const data: PageResponse<Announcement> = await announcementService.getAnnouncementList(page, size);
            setAnnouncements(data.content || []);
            setPagination({
                current: data.number + 1,
                pageSize: data.size,
                total: data.totalElements,
            });
        } catch (error) {
            messageApi.error('获取公告列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (pag: any) => {
        loadAnnouncements(pag.current - 1, pag.pageSize);
    };

    const handleCreate = () => {
        setEditingAnnouncement(null);
        setAnnouncementType('platform');
        setCurrentStep(0);
        setOverlappingWarnings([]);
        setStep1FormData(null);
        setModalVisible(true);
        // Modal打开后再reset和setFieldsValue
        setTimeout(() => {
            form.resetFields();
            formStep2.resetFields();
            form.setFieldsValue({
                title: '',
                type: 'platform',
                effectiveTime: null,
                expireTime: null,
            });
            formStep2.setFieldsValue({
                type: 'platform',
                content: '',
                jumpUrl: '',
            });
        }, 0);
    };

    const handleEdit = (announcement: Announcement) => {
        setEditingAnnouncement(announcement);
        setAnnouncementType(announcement.type || 'platform');
        setCurrentStep(0);
        setOverlappingWarnings([]);
        setStep1FormData(null);
        setModalVisible(true);
        // Modal打开后再reset和setFieldsValue
        setTimeout(() => {
            form.resetFields();
            formStep2.resetFields();
            form.setFieldsValue({
                title: announcement.title,
                type: announcement.type || 'platform',
                effectiveTime: announcement.effectiveTime ? dayjs(announcement.effectiveTime) : null,
                expireTime: announcement.expireTime ? dayjs(announcement.expireTime) : null,
            });
            formStep2.setFieldsValue({
                type: announcement.type || 'platform',
                content: announcement.content,
                jumpUrl: announcement.jumpUrl,
            });
        }, 0);
    };

    const handleDelete = async (id: number) => {
        try {
            await announcementService.deleteAnnouncement(id);
            messageApi.success('删除成功');
            loadAnnouncements(pagination.current - 1, pagination.pageSize);
        } catch (error: any) {
            messageApi.error(error.message || '删除失败');
        }
    };

    const handleNextStep = async () => {
        try {
            const values = await form.validateFields();

            // 保存第一步表单数据
            setStep1FormData(values);

            // 调用后端校验接口
            const validationReq = {
                id: editingAnnouncement?.id,
                title: values.title,
                effectiveTime: values.effectiveTime ? values.effectiveTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
                expireTime: values.expireTime ? values.expireTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
            };

            const result = await announcementService.validateAnnouncement(validationReq);

            if (!result.canContinue) {
                messageApi.error('标题重复，请修改后重试');
                return;
            }

            // 显示时间重叠警告（如果有）
            if (result.overlappingAnnouncements && result.overlappingAnnouncements.length > 0) {
                const warnings = result.overlappingAnnouncements.map(a =>
                    `与"${a.title}"（生效:${a.effectiveTime || '无'}, 失效:${a.expireTime || '无'}）时间段重叠`
                );
                setOverlappingWarnings(warnings);
            }

            setCurrentStep(1);
        } catch (error: any) {
            messageApi.error(error.message || '校验失败');
        }
    };

    const handlePrevStep = () => {
        setCurrentStep(0);
        setOverlappingWarnings([]);
    };

    const handleSubmit = async () => {
        try {
            // 验证第二步
            const step2Values = await formStep2.validateFields();

            // 使用保存的第一步数据
            const step1Values = step1FormData;

            if (!step1Values) {
                messageApi.error('第一步数据丢失，请重新填写');
                return;
            }

            // 构建公告对象
            const announcement: Announcement = {
                title: step1Values.title,
                type: step2Values.type || 'platform',
                content: step2Values.type === 'platform' ? step2Values.content : undefined,
                jumpUrl: step2Values.type === 'jump' ? step2Values.jumpUrl : undefined,
                effectiveTime: step1Values.effectiveTime ? step1Values.effectiveTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
                expireTime: step1Values.expireTime ? step1Values.expireTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
            };

            if (!announcement.title) {
                messageApi.error('标题不能为空');
                return;
            }

            if (editingAnnouncement && editingAnnouncement.id) {
                await announcementService.updateAnnouncement(editingAnnouncement.id, announcement);
                messageApi.success('更新成功');
            } else {
                await announcementService.createAnnouncement(announcement);
                messageApi.success('创建成功');
            }
            setModalVisible(false);
            loadAnnouncements(pagination.current - 1, pagination.pageSize);
        } catch (error: any) {
            messageApi.error(error.message || '操作失败');
        }
    };

    const handleTypeChange = (value: string) => {
        setAnnouncementType(value);
        if (value === 'platform') {
            formStep2.setFieldsValue({jumpUrl: undefined});
        } else {
            formStep2.setFieldsValue({content: undefined});
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setCurrentStep(0);
        setOverlappingWarnings([]);
    };

    const getAnnouncementStatus = (record: Announcement) => {
        const now = dayjs();
        const effectiveTime = record.effectiveTime ? dayjs(record.effectiveTime) : null;
        const expireTime = record.expireTime ? dayjs(record.expireTime) : null;

        if (effectiveTime && now.isBefore(effectiveTime)) {
            return {text: '待生效', color: 'default'};
        }
        if (expireTime && now.isAfter(expireTime)) {
            return {text: '已失效', color: 'error'};
        }
        return {text: '生效中', color: 'success'};
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '标题',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            ellipsis: true,
        },
        {
            title: '类型',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (type: string) => (
                <Tag color={type === 'platform' ? 'blue' : 'green'}>
                    {type === 'platform' ? '平台公告' : '跳转公告'}
                </Tag>
            ),
        },
        {
            title: '状态',
            key: 'status',
            width: 100,
            render: (_: any, record: Announcement) => {
                const status = getAnnouncementStatus(record);
                return <Tag color={status.color}>{status.text}</Tag>;
            },
        },
        {
            title: '生效时间',
            dataIndex: 'effectiveTime',
            key: 'effectiveTime',
            width: 160,
            render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
        },
        {
            title: '失效时间',
            dataIndex: 'expireTime',
            key: 'expireTime',
            width: 160,
            render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_: any, record: Announcement) => (
                <Space>
                    <Tooltip title="编辑">
                        <Button type="link" icon={<EditOutlined/>} onClick={() => handleEdit(record)}/>
                    </Tooltip>
                    <Popconfirm title="确定删除该公告吗？" onConfirm={() => handleDelete(record.id!)}>
                        <Tooltip title="删除">
                            <Button type="link" danger icon={<DeleteOutlined/>}/>
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // 步骤配置（编辑时跳过第一步校验）
    const steps = editingAnnouncement ? [
        {title: '基本信息', content: null},
        {title: '公告内容', content: null},
    ] : [
        {title: '基本信息', content: null},
        {title: '公告内容', content: null},
    ];

    // 渲染第一步表单
    const renderStep1Form = () => (
        <>
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item
                    name="title"
                    label="标题"
                    rules={[{required: true, message: '请输入标题'}]}
                >
                    <Input placeholder="请输入公告标题" maxLength={200}/>
                </Form.Item>

                <Form.Item
                    name="effectiveTime"
                    label="生效时间"
                >
                    <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" placeholder="选择生效时间"/>
                </Form.Item>

                <Form.Item
                    name="expireTime"
                    label="失效时间"
                >
                    <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" placeholder="选择失效时间（可选）"/>
                </Form.Item>
            </Form>

            {overlappingWarnings.length > 0 && (
                <Alert
                    type="warning"
                    message="时间重叠提醒"
                    description={
                        <ul style={{margin: 0, paddingLeft: 20}}>
                            {overlappingWarnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    }
                    style={{marginTop: 16}}
                />
            )}
        </>
    );

    // 渲染第二步表单
    const renderStep2Form = () => (
        <Form form={formStep2} layout="vertical" requiredMark={false}>
            {overlappingWarnings.length > 0 && (
                <Alert
                    type="warning"
                    message="时间重叠提醒"
                    description={
                        <ul style={{margin: 0, paddingLeft: 20}}>
                            {overlappingWarnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    }
                    style={{marginBottom: 16, borderColor: 'var(--pt-bear)', backgroundColor: 'rgba(255,107,107,0.1)'}}
                    showIcon
                />
            )}
            <Form.Item
                name="type"
                label="公告类型"
                rules={[{required: true, message: '请选择公告类型'}]}
                initialValue="platform"
            >
                <Select onChange={handleTypeChange}>
                    <Option value="platform">平台公告</Option>
                    <Option value="jump">跳转公告</Option>
                </Select>
            </Form.Item>

            {announcementType === 'platform' && (
                <Form.Item
                    name="content"
                    label="公告内容"
                    rules={[{required: true, message: '请输入公告内容'}]}
                >
                    <TextArea rows={4} placeholder="请输入公告内容"/>
                </Form.Item>
            )}

            {announcementType === 'jump' && (
                <Form.Item
                    name="jumpUrl"
                    label="跳转链接"
                    rules={[{required: true, message: '请输入跳转链接'}]}
                >
                    <Input placeholder="请输入跳转链接URL"/>
                </Form.Item>
            )}
        </Form>
    );

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 16}}>
                <Button type="primary" icon={<PlusOutlined/>} onClick={handleCreate}>
                    {/*创建公告*/}
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={announcements}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
            />

            <Modal
                title={editingAnnouncement ? '编辑公告' : '创建公告'}
                open={modalVisible}
                onCancel={handleModalCancel}
                width={600}
                footer={
                    editingAnnouncement
                        ? [
                            <Button key="cancel" onClick={handleModalCancel}>取消</Button>,
                            <Button key="prev" onClick={handlePrevStep}
                                    style={{display: currentStep === 1 ? 'inline-block' : 'none'}}>上一步</Button>,
                            <Button key="next" type="primary" onClick={currentStep === 0 ? handleNextStep : handleSubmit}>
                                {currentStep === 0 ? '下一步' : '提交'}
                            </Button>,
                        ]
                        : [
                            <Button key="cancel" onClick={handleModalCancel}>取消</Button>,
                            <Button key="prev" onClick={handlePrevStep}
                                    style={{display: currentStep === 1 ? 'inline-block' : 'none'}}>上一步</Button>,
                            <Button key="next" type="primary" onClick={currentStep === 0 ? handleNextStep : handleSubmit}>
                                {currentStep === 0 ? '下一步' : '提交'}
                            </Button>,
                        ]
                }
            >
                <Steps current={currentStep} items={steps} style={{marginBottom: 24}}/>

                {currentStep === 0 ? renderStep1Form() : renderStep2Form()}
            </Modal>
        </div>
    );
};

export default AnnouncementManagement;
